import { createSocialControls } from '../ui/212-social-controls.mjs';
import { createDirectChoices } from '../ui/15-direct-choices.mjs';
/** Reconcile appearance from accepted settings without rebuilding focused choices. */
export function createThemeChoices(ctx, doc = globalThis.document) {
  const themes = createDirectChoices(ctx, {
    label: ctx.uiText('Color theme'),
    name: 'color-theme',
    variant: 'appearance',
    value: ctx.state.settings.theme,
    options: [
      {
        value: 'system',
        label: 'Match my device',
        shortLabel: ctx.uiText('Device'),
        icon: 'monitor',
      },
      { value: 'light', label: 'Light', icon: 'sun' },
      { value: 'dark', label: 'Dark', icon: 'moon' },
    ],
    onSelect: async (value) => {
      try {
        await ctx.command({ type: 'settings', theme: value }, { paint: false });
        doc.documentElement.dataset.theme = ctx.state.settings.theme;
      } catch (error) {
        if (themes.value === value) themes.setValue(ctx.state.settings.theme);
        ctx.toast(error.message);
      }
    },
  });
  return themes;
}

// Owns settings rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create preference controls and context views from server settings and shared feed policy. */
export function createSettings(ctx) {
  function renderContext() {
    ctx.context.replaceChildren();
    if (ctx.contentScope === 'learning' && ctx.renderLearningContext) {
      ctx.renderLearningContext();
      return;
    }
    const card = ctx.e('section', 'insight-card');
    card.append(
      ctx.e('div', 'eyebrow', 'A FEED THAT LISTENS'),
      ctx.e('h2', '', 'Small signals.\nA gentler rhythm.'),
      ctx.e('p', '', ctx.translate('feed.learningDisclosure')),
    );
    card.append(
      ctx.append(
        ctx.e('div', 'learning-count'),
        ctx.e('strong', '', String(ctx.state.settings.preference?.informativePostIds.length ?? 0)),
        ctx.e('span', '', 'explicit tone signals\n3 posts to learn tone'),
      ),
    );
    const checkbox = ctx.field('input', {
      type: 'checkbox',
      checked: ctx.state.settings.learning,
    });
    checkbox.addEventListener('change', async () => {
      try {
        await ctx.command({ type: 'settings', learning: checkbox.checked }, { paint: false });
        ctx.render();
      } catch (error) {
        ctx.toast(error.message);
      }
    });
    card.append(
      ctx.append(
        ctx.e('label', 'learning-label'),
        checkbox,
        document.createTextNode(ctx.translate('feed.learningLabel')),
      ),
      ctx.button('Reset adaptation', async () => {
        await ctx.command({ type: 'reset-preference' }, { paint: false });
        ctx.render();
        ctx.toast(ctx.translate('feed.learningResetNote'));
      }),
    );
    ctx.context.append(card);
    const circle = ctx.e('section', 'circle-list');
    circle.append(
      ctx.e('h2', '', 'From your close circle'),
      ctx.e('p', 'subtle', 'People first, whatever the tone.'),
    );
    ctx.state.settings.closeFriends
      .filter((id) => !ctx.state.settings.blocked.includes(id) && ctx.state.friends.includes(id))
      .forEach((id) => {
        const user = ctx.who(id);
        if (!user) return;
        const post = ctx.state.posts.find((p) => p.authorId === id);
        const row = ctx.link('', `#/profile/${id}`, 'circle-row');
        row.append(
          ctx.avatar(user),
          ctx.append(
            ctx.e('div'),
            ctx.e('strong', '', user.name),
            ctx.e(
              'p',
              '',
              post
                ? post.text.slice(0, 90) + (post.text.length > 90 ? '…' : '')
                : 'Visit their profile',
            ),
          ),
        );
        circle.append(row);
      });
    circle.append(ctx.link('Manage your circle →', '#/friends', 'text-button'));
    ctx.context.append(
      circle,
      ctx.e(
        'p',
        'legal-note',
        'myAudience public beta · A social network for people 16 and over.\nSample content is labeled. Account and content changes are stored on this service.',
      ),
    );
  }

  function applyShellLanguage() {
    for (const a of document.querySelectorAll('#navigation a')) {
      let span = a.querySelector('.nav-label');
      if (!span) {
        span = ctx.e('span', 'nav-label');
        for (const child of [...a.childNodes])
          if (child.nodeType === Node.TEXT_NODE) child.remove();
        a.insertBefore(span, a.querySelector('small'));
      }
      span.textContent = ctx.translate('nav.' + a.dataset.route);
      a.setAttribute('aria-label', span.textContent);
      a.title = span.textContent;
    }
    const mobileKeys = [
      'nav.wall',
      'nav.learning',
      'nav.friends',
      'nav.explore',
      'nav.messages',
      'nav.activity',
      'nav.settings',
    ];
    document.querySelectorAll('#mobile-nav a').forEach((a, i) => {
      let span = a.querySelector('.nav-label');
      if (!span) {
        span = ctx.e('span', 'nav-label');
        for (const child of [...a.childNodes])
          if (child.nodeType === Node.TEXT_NODE) child.remove();
        a.append(span);
      }
      span.textContent = ctx.translate(mobileKeys[i]);
      a.setAttribute('aria-label', span.textContent);
      a.title = span.textContent;
    });
    const search = document.querySelector('#search-toggle');
    search.setAttribute('aria-label', ctx.translate('common.search'));
    search.title = ctx.translate('common.search');
    const compose = document.querySelector('#compose-nav');
    compose.replaceChildren(
      ctx.icon('plus'),
      ctx.e('span', 'dock-compose-label', ctx.translate('post.share')),
    );
    compose.setAttribute('aria-label', ctx.translate('post.share'));
    compose.title = ctx.translate('post.share');
    const menu = document.querySelector('#navigation-toggle');
    menu.setAttribute('aria-label', ctx.translate('nav.main'));
    menu.title = ctx.translate('nav.main');
    document
      .querySelector('#modal-close')
      .setAttribute('aria-label', ctx.translate('common.close'));
  }

  // Decorative locale cues; native names remain the language authority.
  const flags = { en: '🇬🇧', es: '🇪🇸', 'pt-BR': '🇧🇷', fr: '🇫🇷', de: '🇩🇪' };
  function languageFlag(locale) {
    const flag = ctx.e('span', 'language-flag', flags[locale] ?? '🌐');
    flag.setAttribute('aria-hidden', 'true');
    return flag;
  }
  function languageChoices() {
    const body = ctx.e('div', 'language-choices');
    const codes = { en: 'EN', es: 'ES', 'pt-BR': 'PT', fr: 'FR', de: 'DE' };
    for (const [locale, name] of Object.entries(ctx.LOCALES)) {
      const b = ctx.button(
        '',
        async () => {
          await ctx.command({ type: 'settings', locale });
          if (ctx.modal.open) {
            ctx.modal.close();
            ctx.toast(name);
          }
        },
        'secondary-button',
      );
      b.prepend(languageFlag(locale));
      b.append(ctx.e('span', 'language-code', codes[locale]));
      b.setAttribute('aria-label', name);
      b.title = name;
      b.setAttribute('aria-pressed', String(ctx.state.settings.locale === locale));
      body.append(b);
    }
    return body;
  }

  const settingsTitle = (name, text) =>
    ctx.append(ctx.e('h2'), ctx.icon(name), document.createTextNode(text));
  const settingsAction = (name, label, shortKey, action) => {
    const control = ctx.iconButton(name, label, action);
    control.classList.add('settings-action');
    control.append(ctx.e('span', 'settings-action-label', ctx.translate(shortKey)));
    return control;
  };

  function renderSettings() {
    ctx.view.append(
      ctx.heading('Make this place yours.', 'Your profile, your pace, and your preferences.'),
    );
    const panels = ctx.e('div', 'settings-grid');
    panels.append(createSocialControls(ctx).privacyCard());
    ctx.view.append(panels);
    const signIn = ctx.e('section', 'settings-card');
    const providerStatus = ctx.e('p', 'subtle', 'Checking Google sign-in…');
    signIn.append(settingsTitle('lock', 'Account sign-in'), providerStatus);
    panels.append(signIn);
    ctx
      .api('/api/auth/providers')
      .then((providers) => {
        if (!signIn.isConnected) return;
        providerStatus.textContent = providers.google
          ? 'Link your Google identity to this account. Email matches never link accounts automatically.'
          : 'Google sign-in has not been configured on this server.';
        if (providers.google)
          signIn.append(
            ctx.button(
              'Link Google',
              async () => {
                try {
                  const result = await ctx.api('/api/auth/start', { provider: 'google' });
                  const target = new URL(result.url);
                  if (target.origin !== 'https://accounts.google.com')
                    throw new Error('Invalid provider destination.');
                  window.location.assign(target.href);
                } catch (error) {
                  ctx.toast(error.message);
                }
              },
              'secondary-button provider-button',
            ),
          );
      })
      .catch(() => {
        if (signIn.isConnected)
          providerStatus.textContent =
            'Sign-in provider status is unavailable. Reopen Settings to retry.';
      });
    const learning = ctx.e('section', 'settings-card');
    const checkbox = ctx.field('input', {
      type: 'checkbox',
      checked: ctx.state.settings.learning,
    });
    checkbox.addEventListener('change', async () => {
      try {
        await ctx.command({ type: 'settings', learning: checkbox.checked });
      } catch (error) {
        ctx.toast(error.message);
      }
    });
    learning.append(
      settingsTitle('sparkles', 'Your adaptive feed'),
      ctx.e('p', '', ctx.translate('feed.learningDisclosure')),
      ctx.append(
        ctx.e('label'),
        checkbox,
        document.createTextNode(ctx.translate('feed.learningLabel')),
      ),
      ctx.button('Reset adaptation', async () => {
        await ctx.command({ type: 'reset-preference' });
        ctx.toast(ctx.translate('feed.learningResetNote'));
      }),
    );
    const localeCard = ctx.append(
      ctx.e('section', 'settings-card'),
      settingsTitle('globe', ctx.translate('common.language')),
      ctx.e('p', '', ctx.translate('settings.languageHelp')),
      languageChoices(),
      ctx.e('p', 'settings-language-name', ctx.LOCALES[ctx.state.settings.locale]),
    );
    const appearance = ctx.e('section', 'settings-card');
    const themes = createThemeChoices(ctx);
    appearance.append(settingsTitle('sun', ctx.uiText('Appearance')), themes.node);
    const games = ctx.e('section', 'settings-card');
    const showGames = ctx.field('input', {
      type: 'checkbox',
      checked: ctx.state.settings.gamesFeed !== false,
    });
    showGames.addEventListener('change', async () => {
      try {
        await ctx.command({ type: 'settings', gamesFeed: showGames.checked });
      } catch (error) {
        showGames.checked = ctx.state.settings.gamesFeed !== false;
        ctx.toast(error.message);
      }
    });
    const scoreChoice = () =>
      !ctx.state.settings.recordScores
        ? 'private'
        : ctx.state.settings.anonymousScores !== false
          ? 'anonymous'
          : 'named';
    const scores = createDirectChoices(ctx, {
      label: 'Score privacy',
      name: 'game-score-privacy',
      variant: 'score',
      value: scoreChoice(),
      options: [
        { value: 'private', label: 'Private', icon: 'lock' },
        { value: 'anonymous', label: 'Anonymous', icon: 'eye-off' },
        { value: 'named', label: 'Public name', shortLabel: ctx.uiText('Public'), icon: 'user' },
      ],
      onSelect: async (value) => {
        try {
          await ctx.command(
            {
              type: 'settings',
              recordScores: value !== 'private',
              anonymousScores: value !== 'named',
            },
            { paint: false },
          );
          scores.setValue(scoreChoice());
          ctx.toast(
            value === 'private'
              ? 'Score recording is off. Your published scores were removed.'
              : value === 'anonymous'
                ? 'Future ranked rounds publish anonymously.'
                : 'Future ranked rounds may show your public profile name.',
          );
        } catch (error) {
          scores.setValue(scoreChoice());
          ctx.toast(error.message);
        }
      },
    });
    games.append(
      settingsTitle('gamepad-2', 'A little play'),
      ctx.e(
        'p',
        '',
        'Pocket games can appear between Everyday posts in For you. Scroll past whenever you like.',
      ),
      ctx.append(
        ctx.e('label', 'learning-label'),
        showGames,
        document.createTextNode('Show games in my feed'),
      ),
      scores.node,
      ctx.e(
        'p',
        'subtle',
        'Private is the default: no game session or score record. Choosing Private removes your published scores. Anonymous keeps your name off the table; Public name uses your public display name. Shared game links always open a private round.',
      ),
    );
    const privacy = ctx.e('section', 'settings-card');
    const privacyActions = ctx.e('div', 'settings-actions');
    privacy.append(
      settingsTitle('shield', 'Your boundaries'),
      ctx.e(
        'p',
        '',
        `${ctx.state.settings.hidden.length} hidden posts · ${ctx.state.settings.blocked.length} blocked people`,
      ),
    );
    privacyActions.append(
      ctx.button('Restore hidden posts', () => ctx.command({ type: 'restore' })),
    );
    ctx.state.settings.blocked.forEach((id) =>
      privacyActions.append(
        ctx.button(
          `Unblock ${ctx.who(id).name}`,
          () => ctx.command({ type: 'block', id, blocked: false }),
          'text-button',
        ),
      ),
    );
    privacy.append(privacyActions);
    const reading = ctx.e('section', 'settings-card');
    reading.append(
      settingsTitle('bookmark', 'Your reading place'),
      ctx.e(
        'p',
        '',
        'We remember up to 2,000 viewed post IDs and your last position for the caught-up marker. This history does not teach emotional preferences.',
      ),
      ctx.button('Clear reading history', async () => {
        await ctx.flushSeen();
        await ctx.command({ type: 'forget-reading' });
        ctx.feed.reset(ctx.mode);
        ctx.feedScroll = 0;
        ctx.toast('Reading history cleared.');
      }),
    );
    const account = ctx.e('section', 'settings-card settings-account');
    account.append(
      settingsTitle('user', ctx.translate('setup.accountTitle')),
      ctx.e('p', '', ctx.state.me.email),
      ctx.e('p', '', ctx.translate('setup.accountDescription')),
    );
    const accountActions = ctx.e('div', 'settings-actions settings-launchers');
    accountActions.append(
      settingsAction('pencil', 'Edit profile', 'settings.profile', ctx.editProfile),
      settingsAction('user', ctx.translate('setup.accountSetup'), 'settings.setup', () =>
        ctx.go('welcome'),
      ),
      settingsAction('compass', ctx.translate('tour.replay'), 'settings.tour', () =>
        ctx.go('starter'),
      ),
      settingsAction('shield-check', ctx.translate('setup.verifyEmail'), 'settings.verify', () =>
        ctx.go('verify-email'),
      ),
      settingsAction('log-out', 'Sign out', 'common.signOut', ctx.signOut),
    );
    if (ctx.state.isSuperAdmin)
      accountActions.append(
        settingsAction('shield', 'Administrator dashboard', 'settings.admin', () =>
          ctx.go('admin'),
        ),
      );
    account.append(accountActions);
    panels.append(learning, appearance, games, localeCard, privacy, reading, account);
  }
  return {
    applyShellLanguage,
    languageChoices,
    renderSettings,
    renderContext,
  };
}
